import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { Alert, Button, Snackbar, Stack } from "@mui/material";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  deleteOssDocument,
  fetchOssDocuments,
  fetchOssOrders,
  getOssDocumentDownloadUrl,
  uploadOssDocument,
} from "../../api/ossApi";
import { OssPanel } from "../../components/common/OssPanel";
import { OssMetricCards } from "../../components/common/OssMetricCards";
import { OssPageHeader } from "../../components/common/OssPageHeader";
import { OssDocumentList } from "../../components/documents/OssDocumentList";
import { OssDocumentUploadDialog } from "../../components/documents/OssDocumentUploadDialog";
import { useOssAsyncData } from "../../hooks/useOssAsyncData";

export const OssDocuments = () => {
  const { group_code, project_code } = useParams();
  const [open, setOpen] = useState(false);
  const [toast, setToast] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { data, loading, error, setError, load } = useOssAsyncData({
    fetcher: () => fetchOssDocuments(group_code, project_code, { pageSize: 50 }),
    fallbackData: { items: [], pagination: { total: 0 } },
    errorMessage: "문서 목록을 불러오지 못했습니다.",
    deps: [group_code, project_code],
  });
  const { data: orderData } = useOssAsyncData({
    fetcher: () => fetchOssOrders(group_code, project_code, { pageSize: 100 }),
    fallbackData: { items: [] },
    errorMessage: "주문 목록을 불러오지 못했습니다.",
    deps: [group_code, project_code],
  });
  const metrics = useMemo(() => {
    const rows = data.items || [];
    const orderDocs = rows.filter((row) => !row.order_line_id).length;
    const lineDocs = rows.filter((row) => row.order_line_id).length;
    const latestDocs = rows.filter((row) => Number(row.is_latest ?? 1) === 1).length;
    return [
      { label: "전체 문서", value: Number(data.pagination?.total || rows.length).toLocaleString(), unit: "건", tone: "#6c5ce7", iconName: "folderIcon" },
      { label: "주문 문서", value: orderDocs.toLocaleString(), unit: "건", tone: "#3b82f6", iconName: "folderIcon" },
      { label: "품목 문서", value: lineDocs.toLocaleString(), unit: "건", tone: "#14b8a6", iconName: "checklistIcon" },
      { label: "최신 파일", value: latestDocs.toLocaleString(), unit: "건", tone: "#10b981", iconName: "folderIcon" },
    ];
  }, [data.items, data.pagination?.total]);

  const upload = async (file, payload) => {
    setSubmitting(true);
    setError("");
    try {
      await uploadOssDocument(group_code, project_code, file, payload);
      setOpen(false);
      setToast("문서를 업로드했습니다.");
      await load();
    } catch (e) {
      setError(e.response?.data?.message || "문서 업로드 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (row) => {
    setSubmitting(true);
    setError("");
    try {
      await deleteOssDocument(group_code, project_code, row.document_id);
      setToast("문서를 삭제했습니다.");
      await load();
    } catch (e) {
      setError(e.response?.data?.message || "문서 삭제 중 오류가 발생했습니다.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Stack spacing={2}>
      <OssPageHeader
        title="문서 관리"
        iconName="folderIcon"
        actions={<Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setOpen(true)}>문서 업로드</Button>}
      />
      {error && <Alert severity="warning">{error}</Alert>}
      <OssMetricCards items={metrics} />
      <OssPanel sx={{ p: 1.25, overflow: "hidden", bgcolor: "#fff" }}>
        <OssDocumentList
          rows={data.items}
          loading={loading || submitting}
          onOpen={(row) => window.open(getOssDocumentDownloadUrl(group_code, project_code, row.document_id), "_blank", "noopener,noreferrer")}
          onDelete={remove}
        />
      </OssPanel>
      <OssDocumentUploadDialog open={open} orders={orderData.items} loading={submitting} onClose={() => setOpen(false)} onSubmit={upload} />
      <Snackbar open={Boolean(toast)} autoHideDuration={2500} onClose={() => setToast("")} message={toast} />
    </Stack>
  );
};
